/* START_MODULE search_metadata */
/* START_CONTRACT
PURPOSE: Define bilingual search metadata, structured attribution, and the static route manifest.
MATHEMATICAL_SCOPE: Descriptive metadata only; it must not strengthen the status of mathematical claims.
PUBLIC_SURFACE: SITE_ORIGIN, SITE_NAME, siteOriginForLocale, socialImagesForLocale, seoForPath, indexableRouteSuffixes, alternatePath, orbitPath.
KEYWORDS: seo, prerender, schema-org, copyright, timeline
COMPLEXITY: 3
END_CONTRACT */

import { SINGLE_LOCALE, SITE_ORIGIN, siteOriginForLocale } from "./config";
import { NEWS } from "./content/news";
import { COMMON_PROOFS } from "./content/proofs";
import {
  FAMILIES,
  familyOrbitDescription,
  familySummary,
  findFamilyById,
} from "./lib/families";

export { SITE_ORIGIN, siteOriginForLocale };
export const SITE_NAME = "Magic Squares";
const SITE_AUTHOR = "Vladimir Mynka";

export type SeoLocale = "ru" | "en";

export interface SocialImages {
  alt: string;
  openGraph: string;
  twitter: string;
}

/* START_FUNCTION socialImagesForLocale */
/* START_CONTRACT
PURPOSE: Return absolute localized social-preview image URLs and accessible alternative text.
CONTRACT: Keep Open Graph and Twitter assets aligned with the active route locale and canonical origin.
FAILURE_MEANING: Shared links may show a broken, untranslated, or inaccessible preview image.
KEYWORDS: open-graph, twitter-card, social-preview, localization
COMPLEXITY: 1
END_CONTRACT */
export function socialImagesForLocale(locale: SeoLocale): SocialImages {
  const origin = siteOriginForLocale(locale);
  return {
    alt:
      locale === "ru"
        ? "Magic Squares — исследование магических квадратов 3×3"
        : "Magic Squares — research on 3×3 magic squares",
    openGraph: `${origin}/social/og-${locale}.png`,
    twitter: `${origin}/social/twitter-${locale}.png`,
  };
}
/* END_FUNCTION socialImagesForLocale */

export interface SeoMetadata {
  canonicalPath: string;
  description: string;
  index: boolean;
  lastModified?: string;
  locale: SeoLocale;
  schema: Readonly<Record<string, unknown>>;
  title: string;
}

interface PublicationDates {
  modified: string;
  published: string;
}

interface LocalizedMetadata {
  description: string;
  title: string;
  type?: "Article" | "WebApplication" | "WebPage" | "WebSite";
}

const INITIAL_PUBLICATION: PublicationDates = {
  published: "2026-07-21",
  modified: "2026-07-21",
};

const ROUTE_PUBLICATION_DATES: Readonly<Record<string, PublicationDates>> = {
  "": {
    published: "2026-07-21",
    modified: "2026-07-26",
  },
  "squares-of-squares": {
    published: "2026-07-22",
    modified: "2026-07-22",
  },
  theory: {
    published: "2026-07-22",
    modified: "2026-08-02",
  },
  "proofs/general": {
    published: "2026-07-21",
    modified: "2026-07-27",
  },
  "orbits/5": {
    published: "2026-07-21",
    modified: "2026-07-27",
  },
  "orbits/5/abcdg": {
    published: "2026-07-21",
    modified: "2026-07-27",
  },
  "theory/magic-squares-3x3": {
    published: "2026-07-22",
    modified: "2026-07-22",
  },
  "theory/residues": {
    published: "2026-07-22",
    modified: "2026-07-22",
  },
  "theory/prime-divisors": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "theory/arithmetic-progressions-dir": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "theory/6-9-patterns": {
    published: "2026-07-25",
    modified: "2026-07-26",
  },
  "orbits/6": {
    published: "2026-07-25",
    modified: "2026-07-26",
  },
  "theory/7-9-patterns": {
    published: "2026-07-29",
    modified: "2026-07-29",
  },
  "orbits/7": {
    published: "2026-07-29",
    modified: "2026-07-29",
  },
  "theory/fmn-tfmn": {
    published: "2026-07-23",
    modified: "2026-07-26",
  },
  "theory/early-tf-families": {
    published: "2026-07-23",
    modified: "2026-07-24",
  },
  "theory/f7-plus": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "theory/tfmn-forms": {
    published: "2026-08-02",
    modified: "2026-08-02",
  },
  "theory/elliptic-surfaces-primer": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/f4-plus": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "theory/tf-pair-generation": {
    published: "2026-07-23",
    modified: "2026-07-24",
  },
  "theory/f9-plus": {
    published: "2026-07-24",
    modified: "2026-07-24",
  },
  "theory/f9-plus-elliptic-layers": {
    published: "2026-07-24",
    modified: "2026-07-24",
  },
  "theory/6-9/abcdeh": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abcdej": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abcdfh": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abcdhj": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abcegh-abcegj": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abdefh": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abdefj": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abefgh": {
    published: "2026-07-25",
    modified: "2026-07-25",
  },
  "theory/6-9/abcdef": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/abefgj-abdfhj": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/abcdeg": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/abcdfg": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/abcdgj": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/abcghj": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/6-9/synthesis": {
    published: "2026-07-26",
    modified: "2026-07-26",
  },
  "theory/matrix-algebra/magic-charming-semimagic": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "theory/matrix-algebra/block-structure-split-quaternions": {
    published: "2026-07-23",
    modified: "2026-07-23",
  },
  "lab/6": {
    published: "2026-07-24",
    modified: "2026-07-24",
  },
  news: {
    published: "2026-07-22",
    modified: "2026-07-27",
  },
  timeline: {
    published: "2026-07-22",
    modified: "2026-07-22",
  },
  about: {
    published: "2026-07-22",
    modified: "2026-07-27",
  },
};

function publicationDates(
  suffix: string,
  exactDate?: string,
): PublicationDates {
  if (exactDate) {
    return {
      published: exactDate,
      modified: exactDate,
    };
  }
  return ROUTE_PUBLICATION_DATES[suffix] ?? INITIAL_PUBLICATION;
}

const STATIC_METADATA: Readonly<
  Record<string, Readonly<Record<SeoLocale, LocalizedMetadata>>>
> = {
  "": {
    en: {
      title: "The Open 3×3 Magic Square of Squares Problem",
      description:
        "Research on the open problem of a 3×3 magic square with nine distinct perfect-square entries, its partial k/9 configurations, and related arithmetic and algebraic results.",
      type: "WebSite",
    },
    ru: {
      title: "Открытая задача о магическом квадрате из девяти квадратов",
      description:
        "Исследование открытой задачи о магическом квадрате 3×3 из девяти различных полных квадратов, частичных конфигураций k/9 и связанных арифметических и алгебраических результатов.",
      type: "WebSite",
    },
  },
  "squares-of-squares": {
    en: {
      title: "The 3×3 Magic Square of Squares Problem",
      description:
        "What is a 3×3 magic square of perfect squares, why is the nine-square problem open, and how four- and five-square masks organize partial constructions?",
      type: "Article",
    },
    ru: {
      title: "Задача о магическом квадрате из квадратов 3×3",
      description:
        "Что такое магический квадрат из полных квадратов, почему задача 9/9 остаётся открытой и как маски 4/9 и 5/9 организуют частичные конструкции.",
      type: "Article",
    },
  },
  "lab/6": {
    ru: {
      title: "Калькулятор магических квадратов 6/9 — F4+, F7+ и F9+",
      description:
        "Точный калькулятор конструкций 6/9: пары с одинаковым tf, генераторы F4+ и F9+, эллиптические операции F7+ и два параллельных класса магических квадратов.",
      type: "WebApplication",
    },
    en: {
      title: "6/9 Magic Square Calculator — F4+, F7+, and F9+",
      description:
        "An exact 6/9 construction calculator with equal-tf pairs, F4+ and F9+ generators, F7+ elliptic operations, and two parallel magic-square classes.",
      type: "WebApplication",
    },
  },
  "theory/tf-pair-generation": {
    ru: {
      title: "F7+ и F4+: генерация пар с одинаковым tf",
      description:
        "Полный алгоритм генерации решений tf(a,b)=tf(c,d): F4+ создаёт общий квадратный класс и две начальные точки, а F7+ и групповой закон порождают новые пары.",
      type: "Article",
    },
    en: {
      title: "F7+ and F4+: Generating Parameter Pairs with Equal tf",
      description:
        "A complete workflow for generating tf(a,b)=tf(c,d): F4+ supplies a common square class and two seed points, while F7+ and the group law produce further pairs.",
      type: "Article",
    },
  },
  "theory/f9-plus": {
    ru: {
      title: "F9+: квадратичные подстановки и теорема сокращения",
      description:
        "Полная классификация однородных квадратичных подстановок с точным сокращением f(C,D)/f(a,b), 204 невырожденные ветви и остаточные кривые рода 1.",
      type: "Article",
    },
    en: {
      title: "F9+: Quadratic Substitutions and the Cancellation Theorem",
      description:
        "A complete classification of homogeneous quadratic substitutions with exact cancellation of f(C,D)/f(a,b), 204 nondegenerate branches, and residual genus-one curves.",
      type: "Article",
    },
  },
  "theory/f9-plus-elliptic-layers": {
    ru: {
      title: "Эллиптические слои F9+: от y²=x³−2x к семействам кривых",
      description:
        "Базовая биекция F9, доказательство бесконечности, подлинные однопараметрические семейства квадратичных подстановок и их связь с F4+ и F7+.",
      type: "Article",
    },
    en: {
      title: "The Elliptic Layers of F9+: From y²=x³−2x to Families of Curves",
      description:
        "The basic F9 bijection, a proof of infinitude, genuine one-parameter families of quadratic substitutions, and their relation to F4+ and F7+.",
      type: "Article",
    },
  },
  "theory/6-9/abcdeh": {
    ru: {
      title: "ABCDEH: эллиптическая K3-поверхность для маски 6/9",
      description:
        "Подробный вывод выбранной модели ABCDEH: две прогрессии квадратов, остаточная квартика рода 1, расщеплённый якобиан K3, два независимых сечения и явная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABCDEH: An Elliptic K3 Surface for a 6/9 Pattern",
      description:
        "A detailed derivation of a chosen ABCDEH model: two progressions of squares, a residual genus-one quartic, its split K3 Jacobian, two independent sections, and an explicit parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abcdej": {
    ru: {
      title: "ABCDEJ: эллиптическая K3-поверхность и бесконечное семейство",
      description:
        "Подробный вывод выбранной модели ABCDEJ: две прогрессии квадратов, квартика рода 1, расщеплённый K3-якобиан, неторсионное сечение и полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABCDEJ: An Elliptic K3 Surface and an Infinite Family",
      description:
        "A detailed derivation of a chosen ABCDEJ model: two progressions of squares, a genus-one quartic, its split K3 Jacobian, a non-torsion section, and a polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abcdfh": {
    ru: {
      title: "ABCDFH: палиндромная квартика и эллиптическая K3-поверхность",
      description:
        "Подробный вывод выбранной модели ABCDFH: две прогрессии квадратов, палиндромная квартика рода 1, расщеплённый K3-якобиан, неторсионное сечение и явное полиномиальное семейство.",
      type: "Article",
    },
    en: {
      title: "ABCDFH: A Palindromic Quartic and an Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDFH model: two progressions of squares, a palindromic genus-one quartic, its split K3 Jacobian, a non-torsion section, and an explicit polynomial family.",
      type: "Article",
    },
  },
  "theory/6-9/abcdhj": {
    ru: {
      title: "ABCDHJ: палиндромная квартика и эллиптическая K3-поверхность",
      description:
        "Подробный вывод выбранной модели ABCDHJ: две прогрессии с общей клеткой D, палиндромная квартика, расщеплённый K3-якобиан, неторсионное сечение и явное полиномиальное семейство.",
      type: "Article",
    },
    en: {
      title: "ABCDHJ: A Palindromic Quartic and an Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDHJ model: two progressions sharing D, a palindromic quartic, its split K3 Jacobian, a non-torsion section, and an explicit polynomial family.",
      type: "Article",
    },
  },
  "theory/6-9/abcegh-abcegj": {
    ru: {
      title: "ABCEGH и ABCEGJ: общая эллиптическая K3-поверхность",
      description:
        "Подробный совместный вывод выбранной модели ABCEGH и ABCEGJ: одна квартика рода 1, расщеплённый K3-якобиан 2I₄+8I₂, две независимые секции и общая полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABCEGH and ABCEGJ: A Shared Elliptic K3 Surface",
      description:
        "A detailed joint derivation of a chosen ABCEGH and ABCEGJ model: one genus-one quartic, a split 2I₄+8I₂ K3 Jacobian, two independent sections, and a shared polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abdefh": {
    ru: {
      title: "ABDEFH: три прогрессии и эллиптическая K3-поверхность",
      description:
        "Подробный вывод выбранной модели ABDEFH: правильно твистованная квартика, расщеплённый K3-якобиан 4I₄+4I₂, неторсионное сечение и явная полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABDEFH: Three Progressions and an Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABDEFH model: a correctly twisted quartic, a split 4I₄+4I₂ K3 Jacobian, a non-torsion section, and an explicit polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abdefj": {
    ru: {
      title: "ABDEFJ: цепочка прогрессий и эллиптическая K3-поверхность",
      description:
        "Подробный вывод выбранной модели ABDEFJ: палиндромная квартика, расщеплённый K3-якобиан 4I₄+4I₂, неторсионное сечение и явная полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABDEFJ: A Chain of Progressions and an Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABDEFJ model: a palindromic quartic, a split 4I₄+4I₂ K3 Jacobian, a non-torsion section, and an explicit polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abefgh": {
    ru: {
      title: "ABEFGH: треугольник средних и эллиптическая K3-поверхность",
      description:
        "Подробный вывод выбранной модели ABEFGH: уравнение r(x)r(z)=r(y), обратный образ нормальной формы Лежандра, K3-паспорт 4I₄+4I₂, неторсионная секция и полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABEFGH: A Triangle of Means and an Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABEFGH model: the equation r(x)r(z)=r(y), a Legendre-normal-form pullback, the 4I₄+4I₂ K3 passport, a non-torsion section, and a polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abcdef": {
    ru: {
      title: "ABCDEF: чётная квартика и эллиптическая K3-поверхность 12I₂",
      description:
        "Подробный вывод выбранной модели ABCDEF: прогрессия квадратов, чётная квартика рода 1, расщеплённый K3-якобиан 12I₂, неторсионная секция и явная полиномиальная параметризация.",
      type: "Article",
    },
    en: {
      title: "ABCDEF: An Even Quartic and a 12I₂ Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDEF model: a progression of squares, an even genus-one quartic, a split 12I₂ K3 Jacobian, a non-torsion section, and an explicit polynomial parametrization.",
      type: "Article",
    },
  },
  "theory/6-9/abefgj-abdfhj": {
    ru: {
      title: "ABEFGJ и ABDFHJ: общая Kummer–K3-поверхность tfmn",
      description:
        "Полный совместный вывод двух параллельных масок 6/9: параметризация красных коник, точная достаточность равенства tf, линейная эквивалентность размещений и Kummer-поверхность Km(E×E).",
      type: "Article",
    },
    en: {
      title: "ABEFGJ and ABDFHJ: A Shared tfmn Kummer K3 Surface",
      description:
        "A complete joint derivation of the two parallel 6/9 patterns: parametrized red conics, exact sufficiency of equal tf, a linear equivalence of placements, and the Kummer surface Km(E×E).",
      type: "Article",
    },
  },
  "theory/6-9/abcdeg": {
    ru: {
      title: "ABCDEG: две гауссовы склейки и эллиптическая K3-поверхность 12I₂",
      description:
        "Подробный вывод выбранной модели ABCDEG: центральная прогрессия, чётная квартика рода 1, расщеплённый K3-якобиан 12I₂, неторсионная секция и полиномиальное семейство степени 18.",
      type: "Article",
    },
    en: {
      title: "ABCDEG: Two Gaussian Gluings and a 12I₂ Elliptic K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDEG model: a central progression, an even genus-one quartic, a split 12I₂ K3 Jacobian, a non-torsion section, and a degree-18 polynomial family.",
      type: "Article",
    },
  },
  "theory/6-9/abcdfg": {
    ru: {
      title: "ABCDFG: красная, жёлтая и голубая квадрики на K3-поверхности 12I₂",
      description:
        "Подробный вывод выбранной модели ABCDFG: прогрессия, две различные нормы, чётная квартика рода 1, расщеплённый K3-якобиан 12I₂, неторсионная секция и семейство степени 18.",
      type: "Article",
    },
    en: {
      title: "ABCDFG: Red, Yellow, and Blue Quadrics on a 12I₂ K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDFG model: a progression, two distinct norms, an even genus-one quartic, a split 12I₂ K3 Jacobian, a non-torsion section, and a degree-18 family.",
      type: "Article",
    },
  },
  "theory/6-9/abcdgj": {
    ru: {
      title: "ABCDGJ: одна прогрессия, две гауссовы нормы и K3-поверхность 12I₂",
      description:
        "Подробный вывод выбранной модели ABCDGJ: прогрессия BDJ, две жёлтые нормы, чётная квартика рода 1, расщеплённый K3-якобиан 12I₂, неторсионная секция и семейство степени 18.",
      type: "Article",
    },
    en: {
      title: "ABCDGJ: One Progression, Two Gaussian Norms, and a 12I₂ K3 Surface",
      description:
        "A detailed derivation of a chosen ABCDGJ model: the BDJ progression, two yellow norms, an even genus-one quartic, a split 12I₂ K3 Jacobian, a non-torsion section, and a degree-18 family.",
      type: "Article",
    },
  },
  "theory/6-9/abcghj": {
    ru: {
      title: "ABCGHJ: бескрасная маска и K3-поверхность 4I₄+4I₂",
      description:
        "Подробный вывод выбранной модели ABCGHJ: гладкое пересечение трёх квадрик, гауссова карта, расщеплённая K3-поверхность 4I₄+4I₂, неторсионная секция и семейство степени 14.",
      type: "Article",
    },
    en: {
      title: "ABCGHJ: The Red-Free Pattern and a 4I₄+4I₂ K3 Surface",
      description:
        "A detailed derivation of a chosen ABCGHJ model: a smooth intersection of three quadrics, a Gaussian chart, a split 4I₄+4I₂ K3 surface, a non-torsion section, and a degree-14 family.",
      type: "Article",
    },
  },
  "theory/6-9/synthesis": {
    ru: {
      title: "Общая геометрия 6/9: расширенный атлас K3-поверхностей",
      description:
        "Синтетическая классификация всех 16 типов 6/9: шесть комбинаторных групп, четыре модельных класса, паспорта слоёв, границы геометрического ранга, общие секции и аналоги F4+ и F9+.",
      type: "Article",
    },
    en: {
      title: "The General Geometry of 6/9: An Expanded Atlas of K3 Surfaces",
      description:
        "A synthetic classification of all 16 types of 6/9: six combinatorial groups, four model classes, fiber passports, geometric-rank bounds, generic sections, and F4+/F9+ analogues.",
      type: "Article",
    },
  },
  theory: {
    en: {
      title: "Theory of 3×3 Magic Squares — Contents",
      description:
        "Published chapters on the linear, arithmetic, and Diophantine theory of 3×3 magic squares, with complete proofs and explicit theorem boundaries.",
      type: "WebPage",
    },
    ru: {
      title: "Теория магических квадратов 3×3 — оглавление",
      description:
        "Опубликованные главы по линейной, арифметической и диофантовой теории магических квадратов 3×3 с полными доказательствами и точными границами теорем.",
      type: "WebPage",
    },
  },
  "theory/magic-squares-3x3": {
    en: {
      title: "3×3 Magic Squares — General Form and Classification Proof",
      description:
        "A complete proof that every 3×3 magic square over an abelian group has magic sum 3E and a unique form m(E,x,y), with exact consequences over rings, fields, R, Q, and Z.",
      type: "Article",
    },
    ru: {
      title: "Магические квадраты 3×3 — общая форма и доказательство классификации",
      description:
        "Полное доказательство равенства M=3E и единственной формы m(E,x,y) над абелевой группой, с точными следствиями для колец, полей, R, Q и Z.",
      type: "Article",
    },
  },
  "theory/residues": {
    en: {
      title: "Residues and Quadratic Residues in 3×3 Magic Squares",
      description:
        "Proofs of congruence restrictions modulo 3, 4, 5, and 24 for integral magic squares of squares, plus a Legendre-symbol obstruction for corner entries.",
      type: "Article",
    },
    ru: {
      title: "Вычеты и квадратичные вычеты в магических квадратах 3×3",
      description:
        "Доказательства ограничений по модулям 3, 4, 5 и 24 для целочисленных магических квадратов из квадратов и препятствие для углов через символ Лежандра.",
      type: "Article",
    },
  },
  "theory/prime-divisors": {
    ru: {
      title: "Простые делители минимального квадрата 9/9",
      description:
        "Доказательства факторизации центрального корня и ограничений на простые делители нецентральных корней минимального магического квадрата 9/9.",
    },
    en: {
      title: "Prime divisors in a minimal 9/9 square",
      description:
        "Proofs of the central-root factorization and restrictions on prime divisors of noncentral roots in a minimal 9/9 magic square.",
    },
  },
  "theory/arithmetic-progressions-dir": {
    ru: {
      title: "Арифметические прогрессии квадратов и dir-функция",
      description:
        "Полная параметризация трёх рациональных и целых квадратов в арифметической прогрессии, восемь прогрессий магического квадрата 3×3 и точная формула dir(m,n).",
      type: "Article",
    },
    en: {
      title: "Arithmetic Progressions of Squares and the dir Function",
      description:
        "A complete parametrization of three rational and integral squares in arithmetic progression, the eight progressions in a magic 3×3 square, and the exact dir(m,n) formula.",
      type: "Article",
    },
  },
  "theory/6-9-patterns": {
    ru: {
      title: "Маски 6/9: шестнадцать типов магических квадратов",
      description:
        "Полная классификация 16 позиционных типов 6/9 с доказательством по лемме Бёрнсайда, тремя квадриками каждой маски и двумя параллельными tfmn-классами.",
      type: "Article",
    },
    en: {
      title: "6/9 Magic-Square Patterns: The Sixteen Positional Types",
      description:
        "A complete classification of the 16 positional 6/9 types, with a Burnside-lemma proof, three quadrics for every pattern, and the two parallel tfmn classes.",
      type: "Article",
    },
  },
  "orbits/6": {
    ru: {
      title: "Атлас 6/9: все 16 позиционных типов",
      description:
        "Полный атлас 16 орбит масок 6/9: исходные системы, три независимые квадрики, цветовые сигнатуры и текущий статус параметризации.",
      type: "Article",
    },
    en: {
      title: "6/9 Atlas: All 16 Positional Types",
      description:
        "A complete atlas of the 16 orbits of 6/9 patterns: original systems, three independent quadrics, colored signatures, and parametrization status.",
      type: "Article",
    },
  },
  "theory/7-9-patterns": {
    ru: {
      title: "Маски 7/9: полный тригонометрический атлас",
      description:
        "Полная классификация восьми D₄-орбит 7/9 и обратимая редукция четырёх квадрик каждой маски к одному уравнению на трёх рациональных углах.",
      type: "Article",
    },
    en: {
      title: "7/9 Patterns: A Complete Trigonometric Atlas",
      description:
        "A complete classification of the eight D4 orbits of 7/9 patterns and a reversible reduction of each four-quadric system to one equation in three rational angles.",
      type: "Article",
    },
  },
  "orbits/7": {
    ru: {
      title: "Атлас 7/9: восемь трёхугловых поверхностей",
      description:
        "Восемь позиционных орбит 7/9 с полными базисами квадрик, формулами семи корней и итоговыми уравнениями на трёх рациональных углах.",
      type: "Article",
    },
    en: {
      title: "7/9 Atlas: Eight Three-Angle Surfaces",
      description:
        "Eight positional orbits of 7/9 patterns with complete quadric bases, seven-root formulas, and final equations in three rational angles.",
      type: "Article",
    },
  },
  "theory/fmn-tfmn": {
    ru: {
      title: "fmn и tfmn в задаче 6/9",
      description:
        "Метод fmn и tfmn для двух параллельных прогрессий квадратов в конфигурациях 6/9: функция f, квадратный класс tf, согласование шагов и конгруэнтные кривые.",
      type: "Article",
    },
    en: {
      title: "fmn and tfmn in the 6/9 Magic-Square Problem",
      description:
        "The fmn and tfmn method for two parallel progressions of squares in 6/9 configurations: the function f, the square class tf, difference matching, and congruent-number curves.",
      type: "Article",
    },
  },
  "theory/early-tf-families": {
    ru: {
      title: "Ранние семейства F1–F8: явные совпадения tf",
      description:
        "Полные выводы семейств F1–F4, тождество F7, четвертичный подъём F8 и границы ранней упрощённой классификации совпадений tf.",
      type: "Article",
    },
    en: {
      title: "The Early F1–F8 Families: Explicit tf Coincidences",
      description:
        "Complete derivations of F1–F4, the F7 identity, the F8 quartic lift, and the scope of the early simplified classification of tf coincidences.",
      type: "Article",
    },
  },
  "theory/f7-plus": {
    ru: {
      title: "F7+: биекция пар tfmn с поверхностью конгруэнтных чисел",
      description:
        "Полное доказательство биекции между невырожденными проективными парами фиксированного tf и рациональными точками y²=x³−T²x с точностью до знака y.",
      type: "Article",
    },
    en: {
      title: "F7+: The Bijection Between tfmn Pairs and the Congruent-Number Surface",
      description:
        "A complete proof of the bijection between nondegenerate projective pairs of fixed tf and rational points on y²=x³−T²x, up to the sign of y.",
      type: "Article",
    },
  },
  "theory/tfmn-forms": {
    ru: {
      title: "Целочисленные tfmn-формы: контейнеры, Куммер и групповой закон",
      description:
        "Четыре контейнера tfmn как гармонический репер и целочисленный подъём куммеровых координат: точные законы сложения, удвоения, утроения и связь с матрицей Монски.",
      type: "Article",
    },
    en: {
      title: "Integral tfmn Forms: Containers, Kummer Coordinates, and the Group Law",
      description:
        "The four tfmn containers as a harmonic frame and integral lift of Kummer coordinates, with exact addition, doubling, and tripling laws and their link to the Monsky matrix.",
      type: "Article",
    },
  },
  "theory/elliptic-surfaces-primer": {
    ru: {
      title: "Кривые рода 1, якобианы и эллиптические поверхности",
      description:
        "Введение в якобианы квартик рода 1, эллиптические поверхности, типы Кодайры, K3-поверхности, формулу Шиоды—Тейта и арифметический, геометрический и специализированный ранги.",
      type: "Article",
    },
    en: {
      title: "Genus-One Curves, Jacobians, and Elliptic Surfaces",
      description:
        "An introduction to Jacobians of genus-one quartics, elliptic surfaces, Kodaira fibers, K3 surfaces, the Shioda–Tate formula, and arithmetic, geometric, and specialized ranks.",
      type: "Article",
    },
  },
  "theory/f4-plus": {
    ru: {
      title: "F4+: поверхность пифагоровых площадей и ранг ≥ 2",
      description:
        "Полная модель совпадающих квадратных классов пифагоровых площадей, рациональная эллиптическая поверхность, K3-замена базы и доказательство ранга не меньше 2 универсальной кривой конгруэнтного числа.",
      type: "Article",
    },
    en: {
      title: "F4+: Pythagorean-Area Surface and Generic Rank ≥ 2",
      description:
        "A complete model for equal square classes of Pythagorean areas, its rational elliptic surface and K3 base change, and a proof that the universal congruent-number curve has rank at least 2.",
      type: "Article",
    },
  },
  "theory/matrix-algebra/magic-charming-semimagic": {
    ru: {
      title: "Магические, чарующие и полумагические квадраты 3×3",
      description:
        "Полная классификация полумагических матриц 3×3, ассоциированные и центросимметрические компоненты, четыре закона умножения и точные разложения через магические квадраты.",
      type: "Article",
    },
    en: {
      title: "Magic, Charming, and Semimagic 3×3 Squares",
      description:
        "A complete classification of semimagic 3×3 matrices, their associated and balanced components, four product laws, and exact decompositions into magic squares.",
      type: "Article",
    },
  },
  "theory/matrix-algebra/block-structure-split-quaternions": {
    ru: {
      title: "Блочная структура полумагических матриц и расщеплённые кватернионы",
      description:
        "Доказательство изоморфизма SM₃(K)≅K⊕M₂(K), явный базис 1,i,j,k идеала нулевой суммы, таблица умножения и расщеплённая норма.",
      type: "Article",
    },
    en: {
      title: "Block Structure of Semimagic Matrices and Split Quaternions",
      description:
        "A proof of SM₃(K)≅K⊕M₂(K), an explicit basis 1,i,j,k of the zero-sum ideal, its multiplication table, and the split norm.",
      type: "Article",
    },
  },
  lab: {
    en: {
      title: "3×3 Magic Square Calculator — E, x, y Form",
      description:
        "Build and transform any 3×3 magic square from its unique coordinates E, x, and y, or load a proved four- or five-square parametric family.",
      type: "WebApplication",
    },
    ru: {
      title: "Калькулятор магического квадрата 3×3 — форма E, x, y",
      description:
        "Стройте и преобразуйте магический квадрат 3×3 по его однозначным координатам E, x, y или выбирайте доказанное параметрическое семейство 4/9 и 5/9.",
      type: "WebApplication",
    },
  },
  "proofs/general": {
    en: {
      title: "3×3 Magic Squares with 4 or 5 Square Entries",
      description:
        "A complete D4-orbit classification of four and five perfect-square entries, with necessary-and-sufficient quadratic systems and the globally complete ABCDG algorithm.",
      type: "Article",
    },
    ru: {
      title: "Магические квадраты 3×3 с 4 или 5 квадратными клетками",
      description:
        "Полная классификация D₄-орбит четырёх и пяти квадратных клеток, необходимые и достаточные системы квадрик и глобально полный алгоритм ABCDG.",
      type: "Article",
    },
  },
  "orbits/4": {
    en: {
      title: "Four Square Entries in a 3×3 Magic Square — 23 Orbits",
      description:
        "The 23 D4 symmetry orbits of four guaranteed perfect-square entries in a 3×3 magic square, with their defining quadratic relations.",
      type: "Article",
    },
    ru: {
      title: "Четыре квадратные клетки в магическом квадрате 3×3 — 23 орбиты",
      description:
        "Все 23 орбиты D₄ для четырёх гарантированно квадратных клеток магического квадрата 3×3 и определяющие их квадратичные соотношения.",
      type: "Article",
    },
  },
  "orbits/5": {
    en: {
      title: "Five Square Entries in a 3×3 Magic Square — 23 Orbits",
      description:
        "The 23 D4 symmetry orbits of five guaranteed perfect-square entries, with paired quadrics, parametrizations, coverage proofs, and the complete ABCDG algorithm.",
      type: "Article",
    },
    ru: {
      title: "Пять квадратных клеток в магическом квадрате 3×3 — 23 орбиты",
      description:
        "Все 23 орбиты D₄ для пяти гарантированно квадратных клеток: пары квадрик, параметризации, доказательства покрытия и полный алгоритм ABCDG.",
      type: "Article",
    },
  },
  news: {
    en: {
      title: "Magic Squares Project News",
      description:
        "Research milestones and publications from the Magic Squares project.",
    },
    ru: {
      title: "Новости проекта Magic Squares",
      description:
        "Вехи исследования и публикации проекта Magic Squares.",
    },
  },
  timeline: {
    en: {
      title: "Magic Squares Research Timeline",
      description:
        "The history of the Magic Squares research project, from its first ideas and constructions to its proofs and present form.",
    },
    ru: {
      title: "Хронология исследования Magic Squares",
      description:
        "История исследования Magic Squares: от первых идей и найденных конструкций до доказательств и современной постановки задачи.",
    },
  },
  about: {
    en: {
      title: "About the Magic Squares Research Project",
      description:
        "The 9/9 and 7/9 problems, complete coverage of the 5/9 positional types, progress on 6/9, charming squares, the F4+ elliptic surface, authorship, and rights.",
    },
    ru: {
      title: "О проекте исследования магических квадратов",
      description:
        "Задачи 9/9 и 7/9, полное покрытие позиционных типов 5/9, продвижение для 6/9, чарующие квадраты, эллиптическая поверхность F4+, авторы и права.",
    },
  },
};

/* START_FUNCTION pageSchema */
/* START_CONTRACT
PURPOSE: Build one Schema.org record with consistent authorship and rights metadata.
CONTRACT: Attribute every public page to the three project authors and point redistribution terms to the localized copyright section.
FAILURE_MEANING: Search engines may expose incomplete or incorrect authorship metadata.
KEYWORDS: schema-org, author, copyright
COMPLEXITY: 2
END_CONTRACT */
function pageSchema(
  metadata: LocalizedMetadata,
  canonicalPath: string,
  locale: SeoLocale,
  dates: PublicationDates,
): Readonly<Record<string, unknown>> {
  const type = metadata.type ?? "WebPage";
  const socialImages = socialImagesForLocale(locale);
  const authors = [
    {
      "@type": "Person",
      name: SITE_AUTHOR,
    },
    {
      "@type": "Person",
      name: locale === "ru"
        ? "Автор, пожелавший сохранить анонимность"
        : "An author who wishes to remain anonymous",
    },
    {
      "@type": "Person",
      name: locale === "ru" ? "Алексей Поздеев" : "Alexey Pozdeev",
    },
  ];
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    name: metadata.title,
    description: metadata.description,
    inLanguage: locale,
    url: `${siteOriginForLocale(locale)}${canonicalPath}`,
    image: socialImages.openGraph,
    thumbnailUrl: socialImages.openGraph,
    creator: authors,
    contributor: {
      "@type": "Person",
      name: "Alexey Khalin",
      affiliation: {
        "@type": "Organization",
        name: "IITP RAS",
      },
    },
    copyrightHolder: {
      "@type": "Person",
      name: SITE_AUTHOR,
    },
    copyrightNotice: `© 2021–2026 ${SITE_AUTHOR}`,
    copyrightYear: 2021,
    license: `${siteOriginForLocale(locale)}/${locale}/about#copyright`,
  };
  if (type === "Article") {
    schema.headline = metadata.title;
    schema.author = authors;
    schema.datePublished = dates.published;
    schema.dateModified = dates.modified;
  }
  if (type === "WebApplication") {
    schema.applicationCategory = "EducationalApplication";
    schema.operatingSystem = "Any";
  }
  return schema;
}
/* END_FUNCTION pageSchema */

function localizedStatic(
  suffix: string,
  locale: SeoLocale,
): LocalizedMetadata | undefined {
  return STATIC_METADATA[suffix]?.[locale];
}

function normalizePath(value: string): string {
  const pathname = new URL(value, SITE_ORIGIN).pathname;
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function notFoundMetadata(pathname: string): SeoMetadata {
  const locale = SINGLE_LOCALE ?? (pathname.startsWith("/ru") ? "ru" : "en");
  return {
    canonicalPath: pathname,
    description:
      locale === "ru"
        ? "Запрошенной страницы нет в атласе Magic Squares."
        : "The requested Magic Squares page does not exist.",
    index: false,
    locale,
    schema: {},
    title:
      locale === "ru"
        ? "Страница не найдена — Magic Squares"
        : "Page not found — Magic Squares",
  };
}

export function seoForPath(value: string): SeoMetadata {
  const pathname = normalizePath(value);
  const match = pathname.match(/^\/(ru|en)(?:\/(.*))?$/);
  let locale: SeoLocale;
  let suffix: string;
  if (SINGLE_LOCALE) {
    locale = SINGLE_LOCALE;
    suffix = pathname === "/" ? "" : pathname.replace(/^\//, "");
  } else {
    if (!match) return notFoundMetadata(pathname);
    locale = match[1] as SeoLocale;
    suffix = match[2] ?? "";
  }
  const staticMetadata = localizedStatic(suffix, locale);
  if (staticMetadata) {
    const dates = publicationDates(suffix);
    return {
      canonicalPath: pathname,
      description: staticMetadata.description,
      index: true,
      lastModified: dates.modified,
      locale,
      schema: pageSchema(staticMetadata, pathname, locale, dates),
      title: staticMetadata.title,
    };
  }

  const orbitMatch = suffix.match(/^orbits\/(4|5)\/([a-z]+)$/);
  if (orbitMatch) {
    const level = Number(orbitMatch[1]);
    const family = findFamilyById(orbitMatch[2]);
    if (family?.level === level) {
      const mask = family.title;
      const summary = familySummary(family, locale);
      const orbit = familyOrbitDescription(family, locale);
      const metadata: LocalizedMetadata =
        locale === "en"
          ? {
              title: `${mask}: ${level} Square Entries in a 3×3 Magic Square`,
              description: `${orbit ? `${orbit}. ` : ""}${summary} Includes the explicit parametrization, assumptions, and coverage proof.`,
              type: "Article",
            }
          : {
              title: `${mask}: ${level} квадратные клетки в магическом квадрате 3×3`,
              description: `${orbit ? `${orbit}. ` : ""}${summary} Приведены явная параметризация, условия и доказательство области покрытия.`,
              type: "Article",
            };
      const dates = publicationDates(suffix);
      return {
        canonicalPath: pathname,
        description: metadata.description,
        index: true,
        lastModified: dates.modified,
        locale,
        schema: pageSchema(metadata, pathname, locale, dates),
        title: metadata.title,
      };
    }
  }

  const proofMatch = suffix.match(/^proofs\/([a-z-]+)$/);
  if (proofMatch) {
    const proof = COMMON_PROOFS.find((item) => item.id === proofMatch[1]);
    if (proof) {
      const metadata: LocalizedMetadata = {
        title: locale === "en" ? proof.titleEn : proof.title,
        description: locale === "en" ? proof.summaryEn : proof.summary,
        type: "Article",
      };
      const dates = publicationDates(suffix);
      return {
        canonicalPath: pathname,
        description: metadata.description,
        index: true,
        lastModified: dates.modified,
        locale,
        schema: pageSchema(metadata, pathname, locale, dates),
        title: metadata.title,
      };
    }
  }

  const newsMatch = suffix.match(/^news\/([a-z0-9-]+)$/);
  if (newsMatch) {
    const article = NEWS.find((item) => item.slug === newsMatch[1]);
    if (article) {
      const metadata: LocalizedMetadata = {
        title: locale === "en" ? article.titleEn : article.title,
        description: locale === "en" ? article.summaryEn : article.summary,
        type: "Article",
      };
      const dates = publicationDates(suffix, article.date);
      const schema = {
        ...pageSchema(metadata, pathname, locale, dates),
      };
      return {
        canonicalPath: pathname,
        description: metadata.description,
        index: true,
        lastModified: dates.modified,
        locale,
        schema,
        title: metadata.title,
      };
    }
  }

  return notFoundMetadata(pathname);
}

export function alternatePath(
  canonicalPath: string,
  locale: SeoLocale,
): string {
  return canonicalPath.replace(/^\/(ru|en)(?=\/|$)/, `/${locale}`);
}

export function orbitPath(familyId: string): string {
  const family = findFamilyById(familyId);
  return family ? `/orbits/${family.level}/${family.id}` : "/lab";
}

/* START_FUNCTION indexableRouteSuffixes */
/* START_CONTRACT
PURPOSE: Enumerate every localized route that must be prerendered and indexed.
CONTRACT: Include chronology, static research pages, every orbit, every common proof, and published news only.
FAILURE_MEANING: A declared public route may be omitted from static HTML and the sitemap.
KEYWORDS: route-manifest, sitemap, prerender
COMPLEXITY: 1
END_CONTRACT */
export function indexableRouteSuffixes(): readonly string[] {
  return [
    "",
    "theory",
    "squares-of-squares",
    "theory/magic-squares-3x3",
    "theory/residues",
    "theory/prime-divisors",
    "theory/arithmetic-progressions-dir",
    "theory/6-9-patterns",
    "theory/7-9-patterns",
    "theory/fmn-tfmn",
    "theory/early-tf-families",
    "theory/elliptic-surfaces-primer",
    "theory/f7-plus",
    "theory/tfmn-forms",
    "theory/f4-plus",
    "theory/tf-pair-generation",
    "theory/f9-plus",
    "theory/f9-plus-elliptic-layers",
    "theory/6-9/abcdeh",
    "theory/6-9/abcdej",
    "theory/6-9/abcdfh",
    "theory/6-9/abcdhj",
    "theory/6-9/abcegh-abcegj",
    "theory/6-9/abdefh",
    "theory/6-9/abdefj",
    "theory/6-9/abefgh",
    "theory/6-9/abcdef",
    "theory/6-9/abefgj-abdfhj",
    "theory/6-9/abcdeg",
    "theory/6-9/abcdfg",
    "theory/6-9/abcdgj",
    "theory/6-9/abcghj",
    "theory/6-9/synthesis",
    "theory/matrix-algebra/magic-charming-semimagic",
    "theory/matrix-algebra/block-structure-split-quaternions",
    "lab",
    "lab/6",
    "proofs/general",
    "orbits/4",
    "orbits/5",
    "orbits/6",
    "orbits/7",
    ...FAMILIES.map((family) => `orbits/${family.level}/${family.id}`),
    ...COMMON_PROOFS.map((proof) => `proofs/${proof.id}`),
    "news",
    ...NEWS.map((article) => `news/${article.slug}`),
    "timeline",
    "about",
  ];
}
/* END_FUNCTION indexableRouteSuffixes */

/* END_MODULE search_metadata */
